import "./PDFViewer.css";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import { createUseGesture, pinchAction, dragAction } from "@use-gesture/react";
import { Worker, Viewer, RotateDirection } from "@react-pdf-viewer/core";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import { rotatePlugin } from "@react-pdf-viewer/rotate";
import {
  selectionModePlugin,
  SelectionMode,
} from "@react-pdf-viewer/selection-mode";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/zoom/lib/styles/index.css";
import "@react-pdf-viewer/selection-mode/lib/styles/index.css";
import Button from "@mui/material/Button";

import { handlePDFViewerError } from "@libs/errorLibrary.js";
import PDFErrorPop from "@common-components/PDFErrorPop/PDFErrorPop";
import NavButton from "@common-components/NavButton/NavButton";
import SelectSheet from "@components/SelectSheet/SelectSheet";

function PDFViewer({
  pdfPaths,
  sheetNames,
  currentPdfIndex,
  setCurrentPdfIndex,
  isTablet,
}) {
  const MIN_SCALE = isTablet ? 0.65 : 0.8;
  const MAX_SCALE = 3;
  const ZOOM_STEP = 0.1;

  const containerRef = useRef();
  const useGestureHook = createUseGesture([pinchAction, dragAction]);
  const [gestureKey, setGestureKey] = useState(0); // force remount container for @use-gesture
  const [currentZoom, setCurrentZoom] = useState(MIN_SCALE);
  const zoomRef = useRef(currentZoom);
  zoomRef.current = currentZoom;

  const zoomPluginInstance = zoomPlugin({ enableShortcuts: false });
  const { zoomTo } = zoomPluginInstance;

  const selectionModePluginInstance = selectionModePlugin({
    selectionMode: SelectionMode.Hand,
  });

  const rotatePluginInstance = rotatePlugin();
  const { Rotate } = rotatePluginInstance;

  const validPdfPaths = useMemo(
    () => (Array.isArray(pdfPaths) ? pdfPaths : []),
    [pdfPaths],
  );

  // --- Toast tip ---
  useEffect(() => {
    const hasShownToast = sessionStorage.getItem("aco-pdf-zoom-toast");
    if (!hasShownToast || hasShownToast === "false") {
      toast.info("Tip: Shift + Scroll to Zoom In/Out", {
        autoClose: false,
        toastId: "aco-pdf-zoom-tip",
        onClose: () => sessionStorage.setItem("aco-pdf-zoom-toast", "false"),
      });
      sessionStorage.setItem("aco-pdf-zoom-toast", "true");
    }
  }, []);

  // === Visual viewport scale update ===
  useEffect(() => {
    const updateScaleFactor = () => {
      const scale = window.visualViewport?.scale || 1;
      document.documentElement.style.setProperty(
        "--scale-factor",
        scale.toString(),
      );
    };
    updateScaleFactor();
    window.visualViewport?.addEventListener("resize", updateScaleFactor);
    return () =>
      window.visualViewport?.removeEventListener("resize", updateScaleFactor);
  }, []);

  // === Reset zoom & scroll when PDFindex change ===
  useEffect(() => {
    setCurrentZoom(MIN_SCALE);
    zoomRef.current = MIN_SCALE;
    setGestureKey((k) => k + 1); // force remount container
  }, [currentPdfIndex, MIN_SCALE]);

  // === Finger Gesture handling (User touches screen with 2 fingers) ===
  useGestureHook(
    {
      onDrag: ({ pinching, delta: [dx, dy] }) => {
        if (pinching) return;
        const viewerElem =
          containerRef.current?.querySelector(".rpv-core__viewer");
        if (!viewerElem) return;
        viewerElem.scrollLeft -= dx;
        viewerElem.scrollTop = Math.max(0, viewerElem.scrollTop - dy);
      },
      // movement: [ms] calculates new distance
      onPinch: ({ origin: [ox, oy], first, movement: [ms], memo }) => {
        const viewerElem =
          containerRef.current?.querySelector(".rpv-core__viewer");
        if (!viewerElem) return;

        // Reset memo on first pinch for new PDF (onTouchStart → store initial distance & scale)
        if (first || !memo) {
          return {
            initialZoom: zoomRef.current,
            initialScrollLeft: viewerElem.scrollLeft,
            initialScrollTop: viewerElem.scrollTop,
          };
        }

        // compute scale ratio and apply bounds constraints
        const newZoom = Math.min(
          Math.max(memo.initialZoom * ms, MIN_SCALE),
          MAX_SCALE,
        );

        // update transform state
        setCurrentZoom(newZoom);

        // === cause of zoom in/out functionality frozen ===
        // zoomTo(newZoom);

        const rect = viewerElem.getBoundingClientRect();
        const ratio = newZoom / memo.initialZoom;
        const originX = ox - rect.left + memo.initialScrollLeft;
        const originY = oy - rect.top + memo.initialScrollTop;

        requestAnimationFrame(() => {
          // adjust X/Y position
          viewerElem.scrollLeft = originX * ratio - (ox - rect.left);
          viewerElem.scrollTop = Math.max(0, originY * ratio - (oy - rect.top));
        });

        return memo;
      },
      onPinchEnd: () => zoomTo(zoomRef.current),
      onPinchCancel: () => zoomTo(zoomRef.current),
    },
    {
      target: containerRef,
      pinch: {
        scaleBounds: { min: MIN_SCALE, max: MAX_SCALE },
        rubberband: false,
        pointers: 2,
      },
      drag: { filterTaps: true, threshold: isTablet ? 2 : 10, pointers: 1 },
      eventOptions: { passive: false },
    },
  );

  // ===  Prevent page zoom on Safari ===
  useEffect(() => {
    const handler = (e) => e.preventDefault();
    document.addEventListener("gesturestart", handler);
    return () => document.removeEventListener("gesturestart", handler);
  }, [isTablet]);

  const handleZoom = (zoom) => Math.min(Math.max(zoom, MIN_SCALE), MAX_SCALE);

  const handleWheelZoom = useCallback(
    (event) => {
      if (!event.shiftKey) return;
      event.preventDefault();

      const viewerElem =
        containerRef.current?.querySelector(".rpv-core__viewer");
      if (!viewerElem) return;

      const oldZoom = zoomRef.current;
      const zoomFactor = event.deltaY > 0 ? 1 - ZOOM_STEP : 1 + ZOOM_STEP;
      const newZoom = handleZoom(oldZoom * zoomFactor);
      const ratio = newZoom / oldZoom;

      const rect = viewerElem.getBoundingClientRect();
      const offsetX = event.clientX - rect.left + viewerElem.scrollLeft;
      const offsetY = event.clientY - rect.top + viewerElem.scrollTop;

      setCurrentZoom(newZoom);
      zoomTo(newZoom);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          viewerElem.scrollLeft = offsetX * ratio - (event.clientX - rect.left);
          viewerElem.scrollTop = offsetY * ratio - (event.clientY - rect.top);
        });
      });
    },
    [zoomTo],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener("wheel", handleWheelZoom, { passive: false });
    return () => container.removeEventListener("wheel", handleWheelZoom);
  }, [handleWheelZoom]);

  // --- Nav / Sheet change handler ---
  const handleSetCurrentIndex = (newIndex) => {
    setCurrentPdfIndex(newIndex);
    setCurrentZoom(MIN_SCALE);
    zoomTo(MIN_SCALE);
    setGestureKey((k) => k + 1); // force remount container for new PDF
  };

  const renderPDFViewerError = (error) => (
    <PDFErrorPop errorMessage={handlePDFViewerError(error)} />
  );

  const viewerKey = `pdf-viewer-${currentPdfIndex}-${validPdfPaths[currentPdfIndex]}`;

  return (
    <div className="PDFViewer__container">
      {validPdfPaths.length > 1 && (
        <NavButton
          currentIndex={currentPdfIndex}
          setCurrentIndex={handleSetCurrentIndex}
          currentList={validPdfPaths}
        />
      )}

      <div className="PDFViewer__SheetSelection--container">
        <div className="PDFViewer__SheetSelectionContainer--center">
          <SelectSheet
            sheetNames={sheetNames}
            currentPdfIndex={currentPdfIndex}
            setCurrentIndex={handleSetCurrentIndex}
          />
        </div>
        <div className="PDFViewer__SheetSelectionContainer--rotatebtn">
          <Rotate direction={RotateDirection.Backward}>
            {(props) => (
              <Button
                id="basic-button"
                onClick={props.onClick}
                disableRipple
                sx={{
                  minWidth: "24px",
                  width: "24px",
                  height: "24px",
                  color: "title.main",
                  padding: "0",
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: "primary.btn_hover",
                  },
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 508.21"
                  style={{ transform: "scale(1.15) rotate(-90deg)" }}
                >
                  <path
                    fill="currentColor"
                    d="M491.59 193.72c.87 6.92-4.03 13.24-10.95 14.12-6.91.87-13.24-4.03-14.11-10.95-3.61-28.53-15.97-53.03-34.11-71.6-17.72-18.14-41.03-30.66-67.14-35.76l12.03 20.18c3.58 5.99 1.63 13.75-4.36 17.33-5.99 3.58-13.75 1.63-17.33-4.36l-27.93-46.85c-3.52-5.88-1.72-13.53 4.11-17.19l46.16-29.12c5.91-3.71 13.72-1.94 17.43 3.96 3.72 5.91 1.94 13.72-3.96 17.43l-21.78 13.73c31.4 6.04 59.49 21.11 80.9 43.03 21.83 22.34 36.7 51.8 41.04 86.05zM149.82 508.15c-10.05 0-18.28-8.13-18.28-18.2v-75.81h20.42v73.67c71.91 0 174.74-.02 246.64-.02v-84.82c0-5.7 4.64-10.26 10.26-10.26h82.81V263.58H329.7v-20.42h164.02c10.12 0 18.28 8.17 18.28 18.28v141.53c0 3.12-1.42 5.98-3.75 7.84-10.47 10.76-91.39 97.4-97.43 97.4-76.7 0-184.27-.06-261-.06zm146.13-145.97c0 10.05-8.14 18.28-18.2 18.28H18.29C8.19 380.46 0 372.28 0 362.18V18.28C0 8.16 8.17 0 18.29 0h172.47c3.12 0 5.98 1.42 7.85 3.74l89.99 87.63c4.84 4.84 7.4 8.64 7.4 9.81 0 76.69-.05 184.27-.05 261zm-20.34-2.14c0-71.92-.03-174.75-.03-246.64h-84.82c-5.7 0-10.25-4.64-10.25-10.26V20.33H20.43v339.71h255.18z"
                  />
                </svg>
              </Button>
            )}
          </Rotate>

          <Rotate direction={RotateDirection.Forward}>
            {(props) => (
              <Button
                id="basic-button"
                onClick={props.onClick}
                disableRipple
                sx={{
                  minWidth: "24px",
                  width: "24px",
                  height: "24px",
                  color: "title.main",
                  padding: "0",
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: "primary.btn_hover",
                  },
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 508.21"
                  style={{ transform: "scale(1.15) scaleX(-1) rotate(-90deg)" }}
                >
                  <path
                    fill="currentColor"
                    d="M491.59 193.72c.87 6.92-4.03 13.24-10.95 14.12-6.91.87-13.24-4.03-14.11-10.95-3.61-28.53-15.97-53.03-34.11-71.6-17.72-18.14-41.03-30.66-67.14-35.76l12.03 20.18c3.58 5.99 1.63 13.75-4.36 17.33-5.99 3.58-13.75 1.63-17.33-4.36l-27.93-46.85c-3.52-5.88-1.72-13.53 4.11-17.19l46.16-29.12c5.91-3.71 13.72-1.94 17.43 3.96 3.72 5.91 1.94 13.72-3.96 17.43l-21.78 13.73c31.4 6.04 59.49 21.11 80.9 43.03 21.83 22.34 36.7 51.8 41.04 86.05zM149.82 508.15c-10.05 0-18.28-8.13-18.28-18.2v-75.81h20.42v73.67c71.91 0 174.74-.02 246.64-.02v-84.82c0-5.7 4.64-10.26 10.26-10.26h82.81V263.58H329.7v-20.42h164.02c10.12 0 18.28 8.17 18.28 18.28v141.53c0 3.12-1.42 5.98-3.75 7.84-10.47 10.76-91.39 97.4-97.43 97.4-76.7 0-184.27-.06-261-.06zm146.13-145.97c0 10.05-8.14 18.28-18.2 18.28H18.29C8.19 380.46 0 372.28 0 362.18V18.28C0 8.16 8.17 0 18.29 0h172.47c3.12 0 5.98 1.42 7.85 3.74l89.99 87.63c4.84 4.84 7.4 8.64 7.4 9.81 0 76.69-.05 184.27-.05 261zm-20.34-2.14c0-71.92-.03-174.75-.03-246.64h-84.82c-5.7 0-10.25-4.64-10.25-10.26V20.33H20.43v339.71h255.18z"
                  />
                </svg>
              </Button>
            )}
          </Rotate>
        </div>
      </div>

      <div
        key={`gesture-${gestureKey}`}
        className="PDFViewer__viewer--container"
        ref={containerRef}
      >
        <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js">
          {validPdfPaths[currentPdfIndex] ? (
            <Viewer
              key={viewerKey}
              fileUrl={validPdfPaths[currentPdfIndex]}
              plugins={[
                zoomPluginInstance,
                selectionModePluginInstance,
                rotatePluginInstance,
              ]}
              renderError={renderPDFViewerError}
              defaultScale={MIN_SCALE}
              enableSmoothScroll={true}
              className="PDFViewer__viewer--viewer"
              onDocumentLoad={() => zoomTo(MIN_SCALE)}
            />
          ) : (
            <PDFErrorPop errorMessage={"PDF not available"} />
          )}
        </Worker>
      </div>
    </div>
  );
}

export default PDFViewer;
